/**
 * Endpoints del API de WhatsApp Service
 * Mapeo centralizado de todas las rutas del backend
 */
export const API_ENDPOINTS = {
	// Health
	health: "/health",

	// Auth
	auth: {
		login: "/auth/login",
		refresh: "/auth/refresh",
		logout: "/auth/logout",
		me: "/auth/me",
		changePassword: "/auth/change-password",
	},

	// Users (admin)
	users: {
		list: "/users",
		create: "/users",
		update: (id: string) => `/users/${id}`,
		delete: (id: string) => `/users/${id}`,
	},

	// API Keys
	apiKeys: {
		list: "/api-keys",
		create: "/api-keys",
		delete: (id: string) => `/api-keys/${id}`,
	},

	// Accounts
	accounts: {
		list: "/accounts",
		create: "/accounts",
		byId: (id: string) => `/accounts/${id}`,
		update: (id: string) => `/accounts/${id}`,
		delete: (id: string) => `/accounts/${id}`,
		qr: (id: string) => `/accounts/${id}/qr`,
		status: (id: string) => `/accounts/${id}/status`,
		health: (id: string) => `/accounts/${id}/health`,
		logout: (id: string) => `/accounts/${id}/logout`,
		reconnect: (id: string) => `/accounts/${id}/reconnect`,
		webhookTest: (id: string) => `/accounts/${id}/webhook/test`,
	},

	// Messages
	messages: {
		text: (accountId: string) => `/accounts/${accountId}/messages/text`,
		image: (accountId: string) => `/accounts/${accountId}/messages/image`,
		video: (accountId: string) => `/accounts/${accountId}/messages/video`,
		audio: (accountId: string) => `/accounts/${accountId}/messages/audio`,
		document: (accountId: string) => `/accounts/${accountId}/messages/document`,
		sticker: (accountId: string) => `/accounts/${accountId}/messages/sticker`,
		bulk: (accountId: string) => `/accounts/${accountId}/messages/bulk`,
		bulkThrottled: (accountId: string) =>
			`/accounts/${accountId}/messages/bulk-throttled`,
		poll: (accountId: string) => `/accounts/${accountId}/messages/poll`,
		reaction: (accountId: string) => `/accounts/${accountId}/messages/reaction`,
		// Preflight: validar mime + tamaño ANTES del upload real.
		// Devuelve { allowed, max_bytes, reason }. El backend usa
		// AllowAllMimeValidator hoy; mañana puede whitelist por tenant.
		preflight: (accountId: string) =>
			`/accounts/${accountId}/messages/preflight`,
		read: (accountId: string) => `/accounts/${accountId}/messages/read`,
		status: (accountId: string, msgId: string) =>
			`/accounts/${accountId}/messages/${msgId}/status`,
		forward: (accountId: string) =>
			`/accounts/${accountId}/messages/forward`,
		reply: (accountId: string) =>
			`/accounts/${accountId}/messages/reply`,
	},

	// Chats
	chats: {
		list: (accountId: string) => `/accounts/${accountId}/chats`,
		search: (accountId: string) => `/accounts/${accountId}/chats/search`,
		messages: (accountId: string, chatJID: string) =>
			`/accounts/${accountId}/chats/${encodeURIComponent(chatJID)}/messages`,
		// Soft-delete a single message. Optional body `{revoke: bool}`:
		//   revoke=true (default) → for-everyone via WhatsApp revoke
		//   revoke=false          → local-only soft delete
		// The endpoint is idempotent (second call returns 200 with the
		// same scope). Backend WS event: message.deleted.
		deleteMessage: (accountId: string, messageId: string) =>
			`/accounts/${accountId}/messages/${messageId}`,
		editMessage: (accountId: string, messageId: string) =>
			`/accounts/${accountId}/messages/${messageId}`,
		messageHistory: (accountId: string, messageId: string) =>
			`/accounts/${accountId}/messages/${messageId}/history`,
		exportJson: (accountId: string) => `/accounts/${accountId}/chats/export`,
		exportCsv: (accountId: string) => `/accounts/${accountId}/chats/export/csv`,
		statuses: (accountId: string, chatJID: string) =>
			`/accounts/${accountId}/chats/${encodeURIComponent(chatJID)}/statuses`,
		syncProfilePictures: (accountId: string) =>
			`/accounts/${accountId}/chats/sync-profile-pictures`,
		stickers: (accountId: string, limit = 50) =>
			`/accounts/${accountId}/stickers?limit=${limit}`,
	},

	// Groups
	groups: {
		list: (accountId: string) => `/accounts/${accountId}/groups`,
		sync: (accountId: string) => `/accounts/${accountId}/groups/sync`,
		create: (accountId: string) => `/accounts/${accountId}/groups`,
		byId: (accountId: string, groupJID: string) =>
			`/accounts/${accountId}/groups/${groupJID}`,
		sendMessage: (accountId: string, groupJID: string) =>
			`/accounts/${accountId}/groups/${groupJID}/messages`,
		addParticipants: (accountId: string, groupJID: string) =>
			`/accounts/${accountId}/groups/${groupJID}/participants/add`,
		removeParticipants: (accountId: string, groupJID: string) =>
			`/accounts/${accountId}/groups/${groupJID}/participants/remove`,
		promote: (accountId: string, groupJID: string) =>
			`/accounts/${accountId}/groups/${groupJID}/participants/promote`,
		demote: (accountId: string, groupJID: string) =>
			`/accounts/${accountId}/groups/${groupJID}/participants/demote`,
		leave: (accountId: string, groupJID: string) =>
			`/accounts/${accountId}/groups/${groupJID}/leave`,
	},

	// Scheduled Messages
	scheduled: {
		list: (accountId: string) => `/accounts/${accountId}/scheduled`,
		create: (accountId: string) => `/accounts/${accountId}/scheduled`,
		byId: (accountId: string, schedId: string) =>
			`/accounts/${accountId}/scheduled/${schedId}`,
		update: (accountId: string, schedId: string) =>
			`/accounts/${accountId}/scheduled/${schedId}`,
		cancel: (accountId: string, schedId: string) =>
			`/accounts/${accountId}/scheduled/${schedId}/cancel`,
		delete: (accountId: string, schedId: string) =>
			`/accounts/${accountId}/scheduled/${schedId}`,
	},

	// Reminders
	reminders: {
		list: (accountId: string) => `/accounts/${accountId}/reminders`,
		create: (accountId: string) => `/accounts/${accountId}/reminders`,
		byId: (accountId: string, remId: string) =>
			`/accounts/${accountId}/reminders/${remId}`,
		update: (accountId: string, remId: string) =>
			`/accounts/${accountId}/reminders/${remId}`,
		cancel: (accountId: string, remId: string) =>
			`/accounts/${accountId}/reminders/${remId}/cancel`,
		delete: (accountId: string, remId: string) =>
			`/accounts/${accountId}/reminders/${remId}`,
	},

	// Templates — pertenecen al usuario, no a la cuenta WhatsApp.
	// "send" es la única ruta scoped por cuenta (canal de envío).
	templates: {
		list: () => `/templates`,
		create: () => `/templates`,
		byId: (tplId: string) => `/templates/${tplId}`,
		update: (tplId: string) => `/templates/${tplId}`,
		delete: (tplId: string) => `/templates/${tplId}`,
		send: (accountId: string) => `/accounts/${accountId}/templates/send`,
		aiFields: (tplId: string) => `/templates/${tplId}/ai-fields`,
		aiPreview: () => `/templates/ai-preview`,
	},

	// Contact Lists — pertenecen al tenant. Solo broadcast/* sigue scoped por
	// cuenta porque la cuenta es el canal de envío.
	contactLists: {
		list: () => `/lists`,
		create: () => `/lists`,
		byId: (listId: string) => `/lists/${listId}`,
		update: (listId: string) => `/lists/${listId}`,
		delete: (listId: string) => `/lists/${listId}`,
		addPhones: (listId: string) => `/lists/${listId}/phones/add`,
		addPhonesByLabels: (listId: string) => `/lists/${listId}/phones/add-by-labels`,
		removePhones: (listId: string) => `/lists/${listId}/phones/remove`,
		broadcastText: (accountId: string, listId: string) =>
			`/accounts/${accountId}/lists/${listId}/broadcast/text`,
		broadcastTemplate: (accountId: string, listId: string) =>
			`/accounts/${accountId}/lists/${listId}/broadcast/template`,
	},

	// Labels (CRUD → tenant-scoped; chat associations → account-scoped)
	labels: {
		list: () => `/labels`,
		create: () => `/labels`,
		update: (labelId: string) => `/labels/${labelId}`,
		delete: (labelId: string) => `/labels/${labelId}`,
		assignToChat: (accountId: string, chatJID: string) =>
			`/accounts/${accountId}/chats/${chatJID}/labels`,
		removeFromChat: (accountId: string, chatJID: string, labelId: string) =>
			`/accounts/${accountId}/chats/${chatJID}/labels/${labelId}`,
		chatLabels: (accountId: string, chatJID: string) =>
			`/accounts/${accountId}/chats/${chatJID}/labels`,
		labelChats: (accountId: string, labelId: string) =>
			`/accounts/${accountId}/labels/${labelId}/chats`,
	},

	// Auto Responses
	autoResponses: {
		list: () => `/auto-responses`,
		create: () => `/auto-responses`,
		byId: (arId: string) => `/auto-responses/${arId}`,
		update: (arId: string) => `/auto-responses/${arId}`,
		delete: (arId: string) => `/auto-responses/${arId}`,
	},

	autoResponseExclusions: {
		list: (accountId: string) =>
			`/accounts/${accountId}/auto-response-exclusions`,
		add: (accountId: string) =>
			`/accounts/${accountId}/auto-response-exclusions`,
		remove: (accountId: string, jid: string) =>
			`/accounts/${accountId}/auto-response-exclusions?jid=${encodeURIComponent(jid)}`,
	},

	// AI Provider Catalog — public read endpoint (only providers with a
	// configured API key) for the AI prompts catalog. Admin CRUD lives under
	// adminAIProviders and requires the system-user guard on the backend.
	aiProviders: {
		list: () => `/ai/providers`,
	},

	// Admin — AI provider credentials + per-provider model catalog.
	// All routes require system-user auth.
	adminAIProviders: {
		list: () => `/admin/ai-providers`,
		create: () => `/admin/ai-providers`,
		byId: (id: string) => `/admin/ai-providers/${id}`,
		update: (id: string) => `/admin/ai-providers/${id}`,
		delete: (id: string) => `/admin/ai-providers/${id}`,
		test: (id: string) => `/admin/ai-providers/${id}/test`,
		models: (id: string) => `/admin/ai-providers/${id}/models`,
		createModel: (id: string) => `/admin/ai-providers/${id}/models`,
		modelById: (id: string, modelId: string) =>
			`/admin/ai-providers/${id}/models/${modelId}`,
		updateModel: (id: string, modelId: string) =>
			`/admin/ai-providers/${id}/models/${modelId}`,
		deleteModel: (id: string, modelId: string) =>
			`/admin/ai-providers/${id}/models/${modelId}`,
		setDefaultModel: (id: string, modelId: string) =>
			`/admin/ai-providers/${id}/models/${modelId}/set-default`,
	},

	// AI Prompts — pertenecen al tenant, no a la cuenta WhatsApp.
	aiPrompts: {
		list: () => `/ai-prompts`,
		create: () => `/ai-prompts`,
		byId: (id: string) => `/ai-prompts/${id}`,
		update: (id: string) => `/ai-prompts/${id}`,
		delete: (id: string) => `/ai-prompts/${id}`,
	},

	// Contacts — pertenecen al tenant (libreta del usuario). Solo "check"
	// sigue scoped por cuenta porque usa la sesión WhatsApp para verificar.
	contacts: {
		check: (accountId: string) => `/accounts/${accountId}/contacts/check`,
		list: () => `/contacts`,
		create: () => `/contacts`,
		update: (id: string) => `/contacts/${id}`,
		delete: (id: string) => `/contacts/${id}`,
		bulkDelete: () => `/contacts/bulk-delete`,
		import: () => `/contacts/import`,
	},

	// Profile
	profile: {
		picture: (accountId: string, phone: string) =>
			`/accounts/${accountId}/profile/${phone}/picture`,
		info: (accountId: string) => `/accounts/${accountId}/profile/info`,
		status: (accountId: string) => `/accounts/${accountId}/profile/status`,
	},

	// Analytics
	analytics: {
		account: (accountId: string) => `/accounts/${accountId}/analytics`,
		global: "/analytics",
		health: "/health/accounts",
	},

	// Send Window
	sendWindow: {
		get: (accountId: string) => `/accounts/${accountId}/send-window`,
		set: (accountId: string) => `/accounts/${accountId}/send-window`,
		clear: (accountId: string) => `/accounts/${accountId}/send-window`,
	},

	// Outbox
	outbox: {
		list: (accountId: string) => `/accounts/${accountId}/outbox`,
		getById: (accountId: string, msgId: string) =>
			`/accounts/${accountId}/outbox/${msgId}`,
		enqueue: (accountId: string) => `/accounts/${accountId}/outbox`,
		delete: (accountId: string, msgId: string) =>
			`/accounts/${accountId}/outbox/${msgId}`,
	},

	// Presence
	presence: {
		send: (accountId: string) => `/accounts/${accountId}/presence`,
		sendChat: (accountId: string) => `/accounts/${accountId}/presence/chat`,
		subscribe: (accountId: string) => `/accounts/${accountId}/presence/subscribe`,
	},

	// Media — flujo de 2 pasos: Init (signed URL) + Confirm (cdn_url).
	media: {
		upload: (accountId: string) => `/accounts/${accountId}/media/upload`,
		uploadConfirm: (accountId: string) =>
			`/accounts/${accountId}/media/upload/confirm`,
	},

	// Sessions
	sessions: {
		list: (accountId: string) => `/accounts/${accountId}/sessions`,
	},

	// Events
	events: {
		history: "/events/history",
	},

	// Media Library — flujo de 2 pasos: Init (signed URL) + Confirm.
	mediaLibrary: {
		list: () => `/media-library`,
		uploadUrl: () => `/media-library/upload-url`,
		confirm: () => `/media-library/confirm`,
		delete: (id: string) => `/media-library/${id}`,
	},

	// Flows
	flows: {
		list: () => `/flows`,
		create: () => `/flows`,
		byId: (id: string) => `/flows/${id}`,
		update: (id: string) => `/flows/${id}`,
		delete: (id: string) => `/flows/${id}`,
		toggle: (id: string) => `/flows/${id}/toggle`,
		simulate: (id: string) => `/flows/${id}/simulate`,
		simulateReset: (id: string) => `/flows/${id}/simulate/reset`,
	},

	// Team members — tenant-scoped, solo owner/admin.
	team: {
		list: () => `/tenants/me/members`,
		create: () => `/tenants/me/members`,
		delete: (userId: string) => `/tenants/me/members/${userId}`,
		updateRole: (userId: string) => `/tenants/me/members/${userId}/role`,
	},

	// Conversations — derivación de chats a un agente del tenant.
	conversations: {
		list: "/conversations",
		byId: (conversationId: string) => `/conversations/${conversationId}`,
		messages: (conversationId: string) =>
			`/conversations/${conversationId}/messages`,
		send: (conversationId: string) => `/conversations/${conversationId}/send`,
		notes: (conversationId: string) => `/conversations/${conversationId}/notes`,
		close: (conversationId: string) => `/conversations/${conversationId}/close`,
		assign: (conversationId: string) =>
			`/conversations/${conversationId}/assign`,
		// Deriva una conversación desde el chat crudo (account_id + jid).
		derive: "/conversations/derive",
	},

	// Canned responses — tenant-scoped reply templates.
	cannedResponses: {
		list: "/canned-responses",
		create: () => "/canned-responses",
		byId: (id: string) => `/canned-responses/${id}`,
		update: (id: string) => `/canned-responses/${id}`,
		remove: (id: string) => `/canned-responses/${id}`,
	},


	// Tenant — settings del tenant actual (usuario logueado) y gestión plataforma (system)
	tenant: {
		me: "/tenants/me",
		list: () => "/tenants",
		create: () => "/tenants",
		byId: (id: string) => `/tenants/${id}`,
		deactivate: (id: string) => `/tenants/${id}`,
	},
} as const;
